import { SbImage } from "./StoryblokTypes";

/** Per-language string map, e.g. `{ en: 'A plate of sushi', de: '…' }`. */
type LocalizedText = Record<string, string>;

/**
 * Single asset entry returned by the Scaleflex (Filerobot) asset-picker plugin.
 * `cdn` is the top-level permalink used for rendering. When the picker's
 * `attributes` option includes `meta` (i.e. `"url,meta"`), the DAM metadata is
 * nested under `attributes.meta` — `description`/`alt_text` there carry the
 * human, per-language descriptions used for alt text (see {@link getScaleflexAlt}).
 */
export type ScaleflexFile = {
  cdn: string;
  extension?: string;
  name?: string;
  ownerName?: string;
  source?: string;
  type?: string;
  uuid?: string;
  attributes?: {
    url?: LocalizedText;
    meta?: {
      /** Accessibility alt text maintained in the DAM, per language. */
      alt_text?: LocalizedText;
      /** Human description of the asset, per language. */
      description?: LocalizedText;
      [key: string]: unknown;
    };
  };
};

/**
 * Raw value stored by the scaleflex-asset-picker custom field.
 * Defensive union: the plugin may store a bare array or a `{ asset: [...] }`
 * wrapper — {@link getScaleflexFiles} normalizes both.
 */
export type ScaleflexAssetField = ScaleflexFile[] | { plugin?: string; asset?: ScaleflexFile[] } | null | undefined;

/**
 * Hostname suffixes served by the Scaleflex CDN. Used to tell a Scaleflex CDN
 * URL apart from a Storyblok `a.storyblok.com` URL. `media.ef.com` is EF's
 * Scaleflex/Filerobot CDN CNAME (confirmed from the cdn_permalink returned by
 * the picker); `filerobot.com` is kept as a fallback.
 */
export const SCALEFLEX_HOSTS = ["media.ef.com", "filerobot.com"];

/** True when `src` is a Scaleflex CDN URL (vs a Storyblok `a.storyblok.com` URL). */
export const isScaleflexUrl = (src?: string): boolean => {
  if (!src) return false;
  try {
    const { hostname } = new URL(src);
    return SCALEFLEX_HOSTS.some((host) => hostname.endsWith(host));
  } catch {
    return false;
  }
};

/** Normalize the stored plugin value (bare array or wrapper) to an array of files. */
export const getScaleflexFiles = (field: ScaleflexAssetField): ScaleflexFile[] => {
  if (Array.isArray(field)) return field;
  return field?.asset ?? [];
};

/** Pick the English entry from a localized map, else the first non-empty one. */
const pickLocalized = (text?: LocalizedText): string | undefined =>
  text ? text.en || Object.values(text).find(Boolean) : undefined;

/**
 * Alt text carried by a Scaleflex file: the DAM-maintained description
 * (`attributes.meta.description.en`) describes the image for accessibility.
 * Prefers the dedicated `alt_text` when present, then `description`, English
 * first with a fallback to any other language. Returns undefined when no
 * metadata is present (e.g. the picker's `attributes` option doesn't include
 * `meta`, or the asset has no description).
 */
export const getScaleflexAlt = (file?: ScaleflexFile): string | undefined => {
  const meta = file?.attributes?.meta;
  if (!meta) return undefined;
  return pickLocalized(meta.alt_text) || pickLocalized(meta.description);
};

export type ResolvedAsset = {
  /** URL to render (Scaleflex `cdn` when present, else native `filename`). */
  src: string;
  /** Alt text (see resolution order in {@link resolveAsset}). */
  alt: string;
  /** Whether `src` points at the Scaleflex CDN. */
  isScaleflex: boolean;
};

/**
 * Resolve an asset during the Storyblok → Scaleflex transition.
 *
 * Prefers the Scaleflex picker field when populated, otherwise falls back to
 * the native (deprecated) Storyblok asset — this keeps restored/native content
 * rendering without a frontend rollback.
 *
 * Alt resolution order: explicit `altOverride`, then the Scaleflex file's
 * `meta` alt/description (the DAM-maintained text), then the native asset's
 * alt, then ''.
 */
export const resolveAsset = (
  scaleflexField: ScaleflexAssetField,
  nativeAsset?: Pick<SbImage, "filename" | "alt"> | null,
  altOverride?: string
): ResolvedAsset => {
  const file = getScaleflexFiles(scaleflexField)[0];

  if (file?.cdn) {
    return {
      src: file.cdn,
      alt: altOverride || getScaleflexAlt(file) || nativeAsset?.alt || "",
      isScaleflex: true,
    };
  }
  return {
    src: nativeAsset?.filename || "",
    alt: altOverride || nativeAsset?.alt || "",
    isScaleflex: false,
  };
};

type BuildScaleflexUrlOptions = {
  src: string;
  width?: number;
  height?: number;
  quality?: number;
};

/**
 * Build a Scaleflex CDN transformation URL.
 * Mirrors EF product-sites' params: width and/or height, quality, Optipress
 * level 3, and forced AVIF output (AVIF also makes non-web DAM source formats
 * such as HEIC renderable). Pass only `height` to constrain by height while
 * preserving aspect ratio.
 */
export const buildScaleflexUrl = ({ height, quality = 80, src, width }: BuildScaleflexUrlOptions): string => {
  const params = new URLSearchParams();
  if (width) params.set("w", String(width));
  if (height) params.set("h", String(height));
  params.set("q", String(quality));
  params.set("optipress", "3");
  params.set("force_format", "avif");

  return `${src}${src.includes("?") ? "&" : "?"}${params.toString()}`;
};
